import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FunnelIcon, XMarkIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { useTranslation } from '../hooks/useTranslation';
import { API_URL, getUploadUrl } from '../config/api';
import CurrencyLogo from './CurrencyLogo';
import './css/categoryproducts.css';
import FooterNote from './FooterNote';

const CategoryProducts = () => {
  const { category, branchId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  
  const [products, setProducts] = useState([]);
  const [categoryData, setCategoryData] = useState(null);
  const [branchData, setBranchData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Check if this is a branch-specific category
  const isBranchCategory = !!branchId;
  
  // State for filter
  const [showFilter, setShowFilter] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [descriptionSearch, setDescriptionSearch] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [sortBy, setSortBy] = useState('display_order');
  
  // State for showing ingredients for each product
  const [showIngredients, setShowIngredients] = useState({});

  useEffect(() => {
    fetchData();
  }, [category, branchId, language]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch category
      const categoryKey = category;
      const categoriesRes = await axios.get(`${API_URL}/api/categories`);
      const foundCategory = categoriesRes.data.find(cat => cat.key_name === categoryKey);
      
      if (!foundCategory) {
        setLoading(false);
        return;
      }
      
      setCategoryData(foundCategory);
      
      // Fetch branch if branch-specific
      if (isBranchCategory) {
        try {
          const branchRes = await axios.get(`${API_URL}/api/branches/${branchId}`);
          setBranchData(branchRes.data);
          
          // Get branch categories
          const branchCategories = branchRes.data.categories || [];
          const hasCategory = branchCategories.some(bc => bc.id === foundCategory.id);
          
          if (!hasCategory) {
            setProducts([]);
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error('Error fetching branch:', error);
          setLoading(false);
          return;
        }
      }
      
      // Fetch products
      const productsRes = await axios.get(`${API_URL}/api/products`);
      let filteredProducts = productsRes.data.filter(p => 
        p.category_id === foundCategory.id && p.is_active
      );
      
      // Fetch sizes for each product
      const productsWithSizes = await Promise.all(
        filteredProducts.map(async (product) => {
          try {
            const sizesRes = await axios.get(`${API_URL}/api/products/${product.id}/sizes`);
            product.sizes = sizesRes.data.filter(s => s.is_active);
          } catch (error) {
            product.sizes = [];
          }
          return product;
        })
      );
      
      setProducts(productsWithSizes);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Transform products for display with translations
  const transformedProducts = useMemo(() => {
    return products.map(product => ({
      id: product.id,
      name: language === 'ar' ? product.name_ar : product.name_en,
      description: language === 'ar' ? product.description_ar : product.description_en,
      price: parseFloat(product.price) || 0,
      calories: product.calories || null,
      image: product.image ? getUploadUrl(product.image) : null,
      ingredients: language === 'ar' 
        ? (Array.isArray(product.ingredients_ar) ? product.ingredients_ar : [])
        : (Array.isArray(product.ingredients_en) ? product.ingredients_en : []),
      display_order: product.display_order || 0,
      sizes: product.sizes || []
    }));
  }, [products, language]);

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let filtered = [...transformedProducts];

    // Filter by search term (name or ingredients)
    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.ingredients.some(ing => ing.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filter by description
    if (descriptionSearch) {
      filtered = filtered.filter(product =>
        product.description && product.description.toLowerCase().includes(descriptionSearch.toLowerCase())
      );
    }

    // Filter by price range
    if (minPrice) {
      const min = parseFloat(minPrice);
      if (!isNaN(min)) {
        filtered = filtered.filter(product => product.price >= min);
      }
    }
    if (maxPrice) {
      const max = parseFloat(maxPrice);
      if (!isNaN(max)) {
        filtered = filtered.filter(product => product.price <= max);
      }
    }

    // Sort products
    if (sortBy === 'display_order') {
      // Sort by display_order first, then by id as fallback
      filtered.sort((a, b) => {
        const orderA = a.display_order || 0;
        const orderB = b.display_order || 0;
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        return a.id - b.id;
      });
    } else if (sortBy === 'name') {
      filtered.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === 'ingredients-count') {
      filtered.sort((a, b) => b.ingredients.length - a.ingredients.length);
    } else if (sortBy === 'price-low') {
      filtered.sort((a, b) => a.price - b.price);
    } else if (sortBy === 'price-high') {
      filtered.sort((a, b) => b.price - a.price);
    }

    return filtered;
  }, [transformedProducts, searchTerm, descriptionSearch, minPrice, maxPrice, sortBy]);

  // Toggle ingredients visibility for a specific product

  const categoryName = categoryData 
    ? (language === 'ar' ? categoryData.name_ar : categoryData.name_en)
    : category;

  if (loading) {
    return (
      <section className='main-view'>
        <section className='category-products'>
          <div className='container-fluid'>
            <div className='loading-message'>
              <p>{t('products.loading') || 'Loading...'}</p>
            </div>
          </div>
        </section>
        <FooterNote />
      </section>
    );
  }

  if (!categoryData) {
    return (
      <section className='main-view'>
        <section className='category-products'>
          <div className='container-fluid'>
            <div className='error-message'>
              <p>{t('categories.noResults')}</p>
              <button onClick={() => navigate('/')}>{t('nav.back')}</button>
            </div>
          </div>
        </section>
      <FooterNote />
      </section>
    );
  }

  return (
    <section className='main-view'>
      <section className='category-products'>
        <div className='container-fluid'>
          <div className='category-products-content'>
            {/* Header */}
            <div className='category-header'>
              <button 
                className='back-btn'
                onClick={() => {
                  if (isBranchCategory) {
                    navigate(`/branch/${branchId}`);
                  } else {
                    navigate('/');
                  }
                }}
              >
                {language === 'ar' ? '← ' : ''}{t('nav.back')}{language === 'en' ? ' ←' : ''}
              </button>
              <div>
                {isBranchCategory && branchData && (
                  <p className='branch-context'>
                    {language === 'ar' ? branchData.name_ar : branchData.name_en}
                  </p>
                )}
                <h1 className='category-title'>{categoryName}</h1>
              </div>
            </div>

            {/* Filter Button */}
            <div className='products-header'>
              <button 
                className='open-filter-btn'
                onClick={() => setShowFilter(!showFilter)}
              >
                <FunnelIcon className='icon' />
                <span>{t('products.filter')}</span>
              </button>
            </div>

            {/* Filter Panel */}
            {showFilter && (
              <div className='filter-panel'>
                <div className='filter-header'>
                  <h3>{t('products.filter')}</h3>
                  <button 
                    className='close-filter-btn'
                    onClick={() => setShowFilter(false)}
                  >
                    <XMarkIcon className='icon' />
                  </button>
                </div>
                
                <div className='filter-content'>
                  <div className='filter-group'>
                    <label>{t('products.search')}</label>
                    <input
                      type='text'
                      placeholder={t('products.searchPlaceholder')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className='filter-input'
                    />
                  </div>

                  <div className='filter-group'>
                    <label>{t('products.descriptionSearch')}</label>
                    <input
                      type='text'
                      placeholder={t('products.descriptionPlaceholder')}
                      value={descriptionSearch}
                      onChange={(e) => setDescriptionSearch(e.target.value)}
                      className='filter-input'
                    />
                  </div>

                  <div className='filter-group'>
                    <label>{t('products.priceRange')}</label>
                    <div className='price-range'>
                      <input
                        type='number'
                        placeholder={t('products.minPrice')}
                        value={minPrice}
                        onChange={(e) => setMinPrice(e.target.value)}
                        className='filter-input price-input'
                        min='0'
                        step='0.01'
                      />
                      <span className='price-separator'>-</span>
                      <input
                        type='number'
                        placeholder={t('products.maxPrice')}
                        value={maxPrice}
                        onChange={(e) => setMaxPrice(e.target.value)}
                        className='filter-input price-input'
                        min='0'
                        step='0.01'
                      />
                    </div>
                  </div>

                  <div className='filter-group'>
                    <label>{t('categories.sortBy') || 'Sort By'}</label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className='filter-select'
                    >
                      <option value='display_order'>{t('products.sortByDisplayOrder') || 'Default Order'}</option>
                      <option value='name'>{t('products.sortByName')}</option>
                      <option value='price-low'>{t('products.sortByPriceLow')}</option>
                      <option value='price-high'>{t('products.sortByPriceHigh')}</option>
                      <option value='ingredients-count'>{t('products.sortByIngredients')}</option>
                    </select>
                  </div>

                  <div className='filter-results'>
                    <span>{t('products.results')}: {filteredProducts.length}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Products List */}
            <div className='products-list'>
              <div className='row'>
                {filteredProducts.length > 0 ? (
                  filteredProducts.map((product) => {

                    const handleProductClick = () => {
                      if (isBranchCategory) {
                        navigate(`/branch/${branchId}/category/${category}/product/${product.id}`);
                      } else {
                        navigate(`/${category}/product/${product.id}`);
                      }
                    };

                    return (
                      <div className='col-lg-6 col-md-6 col-6' key={product.id}>
                        <div className='product-item-simple'>
                          {product.image && (
                            <div 
                              className='product-image-wrapper'
                              onClick={handleProductClick}
                            >
                              <img 
                                src={product.image} 
                                alt={product.name} 
                                className='product-image' 
                              />
                            </div>
                          )}
                          <div className='product-info'>
                            <h3 
                              className='product-name'
                              onClick={handleProductClick}
                            >
                              {product.name}
                            </h3>
                            
                            {/* Price */}
                            <div className='product-price'>
                              <span className='price-value'>{product.price.toFixed(2)}</span>
                              <span className='currency'> <CurrencyLogo /></span>
                            </div>

                            {/* More Details Button */}
                            <button
                              className='more-details-btn'
                              onClick={handleProductClick}
                            >
                              {t('products.moreDetails') || 'More Details'}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className='col-6'>
                    <div className='no-products'>
                      <p>{t('products.noProducts')}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Disclaimer */}
            <div className='calories-disclaimer'>
              <p>{t('products.disclaimer')}</p>
            </div>
          </div>
        </div>
      </section>
      <FooterNote />
    </section>
  );
};

export default CategoryProducts;
